import { useState } from "react";
import { ScrollArea } from "../ui/scroll-area";
import { Button } from "../ui/button";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Trail, isHikingTrail, isMantrailingTrail, isDetectionTrail, isCanicrossTrail } from "../../types/trail";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import TrailIcon from "./TrailIcon";
import HikeIcon from "../icon/HikeIcon";
import DetectionIcon from "../icon/DetectionIcon";
import CanicrossIcon from "../icon/CanicrossIcon";

const categoryConfig = {
  mantrailing: { name: 'Mantrailing', nameShort: 'Mantrailing', color: 'blue' },
  hiking: { name: 'Randonnée', nameShort: 'Rando', color: 'green' },
  detection: { name: 'Détection', nameShort: 'Détection', color: 'purple' },
  canicross: { name: 'Canicross', nameShort: 'Canicross', color: 'orange' },
};

function getTrailIcon(category: Trail["category"]) {
  switch (category) {
    case "mantrailing":
      return TrailIcon;
    case "hiking":
      return HikeIcon;
    case "detection":
      return DetectionIcon;
    case "canicross":
      return CanicrossIcon;
    default:
      return HikeIcon;
  }
}

interface TrailListProps {
  trails: Trail[];
  selectedTrailId: string | null;
  onSelectTrail: (trailId: string) => void;
  onCreateNew: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function TrailList({
  trails,
  selectedTrailId,
  onSelectTrail,
  onCreateNew,
  isCollapsed,
  onToggleCollapse,
}: TrailListProps) {
  const [filter, setFilter] = useState<"all" | "mantrailing" | "hiking" | "detection" | "canicross">("all");

  const filteredTrails = trails
    .filter((trail) => {
      if (filter === "all") return true;
      return trail.category === filter;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const groupedTrails = filteredTrails.reduce((acc, trail) => {
    const month = new Date(trail.date).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
    });
    if (!acc[month]) {
      acc[month] = [];
    }
    acc[month].push(trail);
    return acc;
  }, {} as Record<string, Trail[]>);

  const monthToNumber: { [key: string]: number } = {
    janvier: 0,
    février: 1,
    mars: 2,
    avril: 3,
    mai: 4,
    juin: 5,
    juillet: 6,
    août: 7,
    septembre: 8,
    octobre: 9,
    novembre: 10,
    décembre: 11,
  };

  const handleFilterClick = (category: "mantrailing" | "hiking" | "detection" | "canicross") => {
    setFilter((prevFilter) => (prevFilter === category ? "all" : category));
  };

  // Track which months are expanded (all expanded by default)
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>(
    Object.keys(groupedTrails).reduce(
      (acc, month) => ({ ...acc, [month]: true }),
      {}
    )
  );

  const toggleMonth = (month: string) => {
    setExpandedMonths((prev) => ({ ...prev, [month]: !prev[month] }));
  };

  const mantrailingCount = trails.filter(
    (t) => t.category === "mantrailing"
  ).length;
  const hikingCount = trails.filter((t) => t.category === "hiking").length;
  const detectionCount = trails.filter((t) => t.category === "detection").length;
  const canicrossCount = trails.filter((t) => t.category === "canicross").length;

  if (isCollapsed) {
    return (
      <div className="flex flex-col h-full bg-gradient-to-b from-blue-600 via-blue-700 to-blue-800 text-white">
        {/* Fixed Header - Collapsed */}
        <div className="flex-shrink-0 p-2 flex flex-col items-center gap-3 border-b border-white/10">
          <div style={{ height: "40px" }}>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={onCreateNew}
                      size="icon"
                      className="bg-white text-blue-600 hover:bg-blue-50 shadow-md"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Créer une nouvelle piste</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
          </div>

          <Button
            onClick={onToggleCollapse}
            size="icon"
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable Trail Icons */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className="px-2 py-2 space-y-2">
            <TooltipProvider>
              {filteredTrails.map((trail: Trail) => {
                const Icon = getTrailIcon(trail.category);
                const categoryInfo = categoryConfig[trail.category as keyof typeof categoryConfig] || categoryConfig.hiking;
                return (
                  <Tooltip key={trail.id || trail._id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() =>
                          onSelectTrail(trail.id || trail._id || "")
                        }
                        className={`w-full p-2.5 rounded-lg transition-colors flex items-center justify-center border-l-2 ${
                          selectedTrailId === (trail.id || trail._id)
                            ? `bg-${categoryInfo.color}-500/30 backdrop-blur-sm border-${categoryInfo.color}-300`
                            : `hover:bg-white/10 border-${categoryInfo.color}-400/50`
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <div className="flex items-center gap-2">
                        <p className="text-xs">
                          {isMantrailingTrail(trail) && typeof trail.dog === "object"
                            ? trail.dog.name
                            : isCanicrossTrail(trail) && typeof trail.dogId === "object"
                            ? trail.dogId.name
                            : isHikingTrail(trail)
                            ? trail.name
                            : ""}
                        </p>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded bg-${categoryInfo.color}-100 text-${categoryInfo.color}-700`}
                        >
                          {categoryInfo.nameShort}
                        </span>
                      </div>
                      <p className="text-xs opacity-75 mt-0.5">
                        {new Date(trail.date).toLocaleDateString("fr-FR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-blue-600 via-blue-700 to-blue-800 text-white">
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="flex items-center gap-2">
            <Button
              onClick={onCreateNew}
              className="flex-1 bg-white text-blue-600 hover:bg-blue-50 shadow-md gap-2"
            >
              <Plus className="h-4 w-4" />
              Créer une nouvelle piste
            </Button>
          <div className="ml-auto">
            <Button
              onClick={onToggleCollapse}
              size="icon"
              variant="ghost"
              className="text-white hover:bg-white/10 flex-shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Statistics - Always visible */}
        <div className="mt-4">
          <h3 className="mb-2 text-blue-100 text-sm">Statistiques</h3>
          <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 space-y-1 border border-white/20">
            <button
              onClick={() => handleFilterClick("mantrailing")}
              className={`w-full flex items-center gap-2 text-sm p-1 rounded-md transition-colors ${
                filter === "mantrailing"
                  ? "bg-white/20"
                  : "hover:bg-white/10"
              }`}
            >
              <TrailIcon className="h-4 w-4" />
              <span>{mantrailingCount} Mantrailing</span>
            </button>
            <button
              onClick={() => handleFilterClick("hiking")}
              className={`w-full flex items-center gap-2 text-sm p-1 rounded-md transition-colors ${
                filter === "hiking" ? "bg-white/20" : "hover:bg-white/10"
              }`}
            >
              <HikeIcon className="h-4 w-4" />
              <span>{hikingCount} Randonnées</span>
            </button>
            <button
              onClick={() => handleFilterClick("detection")}
              className={`w-full flex items-center gap-2 text-sm p-1 rounded-md transition-colors ${
                filter === "detection" ? "bg-white/20" : "hover:bg-white/10"
              }`}
            >
              <DetectionIcon className="h-4 w-4" />
              <span>{detectionCount} Détections</span>
            </button>
            <button
              onClick={() => handleFilterClick("canicross")}
              className={`w-full flex items-center gap-2 text-sm p-1 rounded-md transition-colors ${
                filter === "canicross" ? "bg-white/20" : "hover:bg-white/10"
              }`}
            >
              <CanicrossIcon className="h-4 w-4" />
              <span>{canicrossCount} Canicross</span>
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Trail List */}
      <ScrollArea className="flex-1 overflow-auto">
        <div
          className="w-full min-w-0 px-4 py-4 space-y-4"
          style={{ width: "calc(min(20rem, 88vw) - 1px)" }}
        >
          {Object.entries(groupedTrails)
            .sort(([monthA], [monthB]) => {
              const [monthStrA, yearA] = monthA.split(" ");
              const [monthStrB, yearB] = monthB.split(" ");
              const dateA = new Date(
                parseInt(yearA),
                monthToNumber[monthStrA.toLowerCase()]
              );
              const dateB = new Date(
                parseInt(yearB),
                monthToNumber[monthStrB.toLowerCase()]
              );
              return dateB.getTime() - dateA.getTime();
            })
            .map(([month, monthTrails]) => (
            <Collapsible
              key={month}
              open={expandedMonths[month]}
              onOpenChange={() => toggleMonth(month)}
            >
              <div>
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 mb-3 w-full text-left text-blue-100 hover:text-white transition-colors group">
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        expandedMonths[month] ? "rotate-0" : "-rotate-90"
                      }`}
                    />
                    <h3 className="text-sm">{month}</h3>
                    <span className="ml-auto bg-white/20 px-2 py-0.5 rounded text-xs">
                      {monthTrails.length}
                    </span>
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 pb-2">
                  {monthTrails.map((trail) => {
                    const Icon = getTrailIcon(trail.category);
                    const categoryInfo = categoryConfig[trail.category as keyof typeof categoryConfig] || categoryConfig.hiking;
                    const isSelected =
                      selectedTrailId === (trail.id || trail._id);
                    return (
                      <button
                        key={trail.id || trail._id}
                        onClick={() =>
                          onSelectTrail(trail.id || trail._id || "")
                        }
                        className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-2 border-l-4 ${
                          isSelected
                            ? `bg-white text-${categoryInfo.color}-700 shadow-md border-${categoryInfo.color}-500`
                            : `hover:bg-white/10 border-${categoryInfo.color}-400/50`
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 flex-shrink-0 ${
                            isSelected ? `text-${categoryInfo.color}-600` : ""
                          }`}
                        />{" "}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="min-w-0 truncate text-sm">
                              {(isMantrailingTrail(trail) || isDetectionTrail(trail)) && typeof trail.dog === "object"
                                ? trail.dog.name
                                : isCanicrossTrail(trail) && typeof trail.dogId === "object"
                                ? trail.dogId.name
                                : isHikingTrail(trail)
                                ? trail.name
                                : ""}
                            </div>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                isSelected
                                  ? `bg-${categoryInfo.color}-100 text-${categoryInfo.color}-700`
                                  : `bg-${categoryInfo.color}-400/30 text-${categoryInfo.color}-100`
                              }`}
                            >
                              {categoryInfo.nameShort}
                            </span>
                          </div>
                          <div className="text-xs opacity-75 mt-0.5 truncate">
                            {trail.location ||
                              new Date(trail.date).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "2-digit",
                              })}
                            • {Math.round(trail.distance || 0)}m
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
